/**
 * The checkbox list both panels use.
 *
 * `target` and `sshTarget` are rendered as separate lines rather than folded
 * into the label, because on the import side this list is the ONLY thing
 * between a hostile file and a credential: the user has to be able to read
 * where each connection points, and to notice a bastion that was not there
 * before. Same component on the export side, so what you tick to send is
 * displayed exactly like what you tick to receive.
 *
 * Not instrumented per checkbox — transient selection for one action, already
 * summarised in the success event's count (see `SettingsGroupChecklist` for
 * the same reasoning).
 */
import { ShieldAlert } from 'lucide-react';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';

export interface PickRow {
  id: string;
  label: string;
  /** Where it connects — host:port, or the file for SQLite. */
  target: string;
  /** Second hop, when the connection routes through an SSH bastion. */
  sshTarget?: string;
  /** e.g. 'pgsql · shop · app'. */
  detail?: string;
  /** Short right-aligned note ('already saved', 'has a password'). */
  note?: string;
  /**
   * How the credential travels ('TLS', 'TLS off', 'TLS not requested'…).
   * Added for review C1: the AAD now authenticates these flags, but a field
   * that is authenticated and invisible only proves the file was not changed,
   * not that the user agreed to what it says.
   */
  transport?: string;
  /** Renders `transport` as a warning — weaker than verified TLS. */
  transportWeak?: boolean;
}

export function ConnectionPickList({
  rows,
  selected,
  onToggle,
  idPrefix,
  emptyLabel,
}: {
  rows: PickRow[];
  selected: ReadonlySet<string>;
  onToggle: (id: string, next: boolean) => void;
  idPrefix: string;
  emptyLabel: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="text-xs text-muted-foreground" data-testid={`${idPrefix}-empty`}>
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
      {rows.map((row) => (
        <div key={row.id} className="flex items-start gap-2" data-testid={`${idPrefix}-row`}>
          <Checkbox
            id={`${idPrefix}-${row.id}`}
            checked={selected.has(row.id)}
            onCheckedChange={(next) => onToggle(row.id, next === true)}
            data-testid={`${idPrefix}-checkbox-${row.id}`}
            className="mt-0.5"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <Label
                htmlFor={`${idPrefix}-${row.id}`}
                className="cursor-pointer truncate text-sm font-normal"
              >
                {row.label}
              </Label>
              {row.note ? (
                <span className="shrink-0 text-[11px] text-muted-foreground">{row.note}</span>
              ) : null}
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">{row.target}</div>
            {row.sshTarget ? (
              <div className="truncate font-mono text-xs text-warning">
                via SSH {row.sshTarget}
              </div>
            ) : null}
            {row.transport ? (
              <div
                className={
                  row.transportWeak
                    ? 'flex items-center gap-1 truncate text-xs text-warning'
                    : 'truncate text-xs text-muted-foreground'
                }
                data-testid={`${idPrefix}-transport-${row.id}`}
              >
                {row.transportWeak ? <ShieldAlert className="h-3 w-3 shrink-0" /> : null}
                {row.transport}
              </div>
            ) : null}
            {row.detail ? (
              <div className="truncate text-xs text-muted-foreground">{row.detail}</div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
